/**
 * ONCALL-SCHED-01a — Team → On-Call: the on-call calendar, built day by day.
 *
 * The same monthly sheet the office sends CEAS today (Sunday-first month grid,
 * PRIMARY over BACKUP in every day), built in Pegasus for whatever stretch
 * the manager picks. Tap a day to set the pair; hold a pair and paint it
 * across days (click or drag); "Fill empty days…" lays a rotation over the
 * empty days only — a helper, never a rule. A day with one name is allowed
 * and shows "no backup" quietly. Publish makes the period visible to every
 * tech (phone banners, Today, the Board); edits after Publish are logged and
 * still allowed. The fairness strip counts days — the room decides what is
 * fair. Design rules: proposal artifact "Pegasus On-Call Schedule" v2.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  CalendarDays, ChevronLeft, ChevronRight, Loader2, Moon, Paintbrush, Plus, Upload,
  Send, Sparkles, Trash2, Wand2, X, CalendarOff, AlertTriangle, ArrowLeftRight, Check, Printer, Mail,
} from "lucide-react";
import { supabase } from "@/lib/supabase";
import { renderOncallSheetHtml, useOncallContacts } from "./OncallPrintView";
import { ImportSheetModal } from "./OncallImport";
import { useTechAvailability, availOutLabel, type AvailEvent } from "@/features/board/availability";
import { TechAvatar } from "@/components/TechAvatar";
import { Card } from "@/components/ui/Card";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { EmptyState } from "@/components/ui/EmptyState";
import { InfoTip } from "@/components/InfoTip";
import { cn } from "@/lib/cn";
import { useEntryFormKeys } from "@/lib/keys";
import { useCan } from "@/lib/permissions/PermissionProvider";
import { useOrgSettingJson, useSaveOrgSetting } from "@/lib/orgSettings";
import {
  useOncallPeriods, useCreateOncallPeriod, useUpdateOncallPeriod, usePublishOncallPeriod, useDeleteOncallPeriod,
  useOncallDays, useUpsertOncallDays, useClearOncallDays, useServiceTechs, techFirstName,
  fairness, isoDate, parseISO, addDaysISO, addMonthsISO, monthStartISO, monthEndISO, fmtShort, fmtMonth, fmtWeekday,
  periodLabel, isWeekendISO, blocksOf, blockLabel,
  useOncallGaps, useOncallSwapRequests, useDecideOncallSwap, useMarkSentToCeas, changedSinceSent,
  type OncallDay, type OncallDayInput, type OncallPeriod, type OncallGap, type OncallSwapRequest,
} from "./oncall";

const WEEKDAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

/** Blue = primary, orange = backup — the two roles, the same two colors on every surface. */
export function PairChip({ name, role, small }: { name: string; role: "primary" | "backup" | "none"; small?: boolean }) {
  return (
    <span className={cn(
      "inline-flex max-w-full items-center gap-1.5 rounded-full font-semibold leading-tight",
      small ? "px-2 py-px text-[11.5px]" : "px-2.5 py-0.5 text-[12.5px]",
      role === "primary" && "bg-blue-50 text-blue-700 ring-1 ring-inset ring-blue-200/70",
      role === "backup" && "bg-orange-50 text-orange-700 ring-1 ring-inset ring-orange-200/70",
      role === "none" && "bg-slate-100 text-slate-400",
    )}>
      {role !== "none" && <span className={cn("h-1.5 w-1.5 shrink-0 rounded-full", role === "primary" ? "bg-blue-500" : "bg-orange-500")} />}
      <span className="truncate">{name}</span>
    </span>
  );
}

export function OncallPane() {
  const canBuild = useCan("team.oncall_build");
  const canSwap = useCan("team.oncall_swap");
  const canEdit = canBuild || canSwap;
  const canSettings = useCan("settings.manage");
  const { data: periods, isLoading: periodsLoading } = useOncallPeriods();
  const { techs } = useServiceTechs();
  const holidays = useOrgSettingJson<string[]>("payroll_holidays", []).data ?? [];
  const holidaySet = useMemo(() => new Set(holidays), [holidays]);
  const nameOf = (empno: string | null | undefined) => {
    if (!empno) return "";
    const t = techs.find((x) => x.empno === empno);
    return t ? techFirstName(t) : empno;
  };

  const today = isoDate(new Date());
  const [editing, setEditing] = useState<string | null>(null);
  // ONCALL-SCHED-01b: /team?pane=oncall&day=2026-09-29 (from a gap card or an
  // approval row) lands on that day with its editor open.
  const [searchParams, setSearchParams] = useSearchParams();
  const jumpDay = searchParams.get("day");
  const [periodId, setPeriodId] = useState<string | null>(null);
  const period: OncallPeriod | null = useMemo(() => {
    if (!periods || periods.length === 0) return null;
    if (periodId) return periods.find((p) => p.id === periodId) ?? null;
    if (jumpDay) { const j = periods.find((p) => p.starts_on <= jumpDay && p.ends_on >= jumpDay); if (j) return j; }
    return periods.find((p) => p.starts_on <= today && p.ends_on >= today) ?? periods[0];
  }, [periods, periodId, today, jumpDay]);

  const [month, setMonth] = useState<string | null>(null);
  const monthISO = useMemo(() => {
    if (month) return month;
    if (jumpDay) return monthStartISO(jumpDay);
    if (!period) return monthStartISO(today);
    if (period.starts_on <= today && period.ends_on >= today) return monthStartISO(today);
    return monthStartISO(period.starts_on);
  }, [month, period, today, jumpDay]);
  useEffect(() => { setMonth(null); }, [period?.id]);
  const { data: days } = useOncallDays(period?.starts_on ?? today, period?.ends_on ?? today, !!period);
  useEffect(() => {
    if (!jumpDay || !period || !days) return; // wait for the days, so the editor opens on the real pair
    // Pin the period + month first: once the param is gone the defaults would
    // pull the view back to today's period.
    setPeriodId(period.id);
    setMonth(monthStartISO(jumpDay));
    setEditing(jumpDay);
    const next = new URLSearchParams(searchParams); next.delete("day");
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jumpDay, period?.id, !!days]);
  const dayMap = useMemo(() => new Map((days ?? []).map((d) => [d.day, d])), [days]);
  // ONCALL-SCHED-01b: published days whose tech is out (approved = gap, requested = waiting)
  const { data: gaps } = useOncallGaps(period?.starts_on ?? today, period?.ends_on ?? today, !!period && period.status === "published");
  const gapMap = useMemo(() => {
    const m = new Map<string, OncallGap[]>();
    for (const g of gaps ?? []) { const l = m.get(g.day) ?? []; l.push(g); m.set(g.day, l); }
    return m;
  }, [gaps]);
  const gapCount = (gaps ?? []).filter((g) => g.status === "approved").length;

  const upsert = useUpsertOncallDays();
  const clear = useClearOncallDays();
  const publish = usePublishOncallPeriod();
  const update = useUpdateOncallPeriod();
  const del = useDeleteOncallPeriod();

  const [paint, setPaint] = useState<{ primary: string; backup: string | null } | null>(null);
  const [newOpen, setNewOpen] = useState(false);
  const [fillOpen, setFillOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false); // ONCALL-IMPORT-01: Upload a Sheet
  const [holOpen, setHolOpen] = useState(false);
  const [confirmPublish, setConfirmPublish] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [ceasOpen, setCeasOpen] = useState(false);
  const markSent = useMarkSentToCeas();
  const changed = period ? changedSinceSent(period, days ?? []) : [];

  // Drag-painting: mouse down on a day, sweep, release → one upsert.
  const drag = useRef<{ active: boolean; set: Set<string> }>({ active: false, set: new Set() });
  const [dragSet, setDragSet] = useState<Set<string>>(new Set());
  const inPeriod = (iso: string) => !!period && iso >= period.starts_on && iso <= period.ends_on;
  const applyPaint = (isos: string[]) => {
    if (!paint || !canEdit) return;
    const rows: OncallDayInput[] = isos.filter(inPeriod).map((day) => ({ day, primary_empno: paint.primary, backup_empno: paint.backup, note: dayMap.get(day)?.note ?? null, source: "edit" }));
    if (rows.length) upsert.mutate(rows);
  };
  useEffect(() => {
    const up = () => {
      if (!drag.current.active) return;
      drag.current.active = false;
      const picked = [...drag.current.set];
      drag.current.set = new Set();
      setDragSet(new Set());
      if (picked.length) applyPaint(picked);
    };
    window.addEventListener("mouseup", up);
    return () => window.removeEventListener("mouseup", up);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paint, period?.id, dayMap]);

  const onDayMouseDown = (iso: string) => {
    if (!inPeriod(iso) || !canEdit) return;
    if (!paint) { setEditing(iso); return; }
    drag.current.active = true;
    drag.current.set = new Set([iso]);
    setDragSet(new Set([iso]));
  };
  const onDayMouseEnter = (iso: string) => {
    if (!drag.current.active || !inPeriod(iso)) return;
    drag.current.set.add(iso);
    setDragSet(new Set(drag.current.set));
  };

  // Month grid (Sunday-first, like the sheet).
  const grid = useMemo(() => {
    const first = parseISO(monthISO);
    const lead = first.getDay();
    const end = monthEndISO(monthISO);
    const cells: (string | null)[] = Array.from({ length: lead }, () => null);
    for (let d = monthISO; d <= end; d = addDaysISO(d, 1)) cells.push(d);
    while (cells.length % 7) cells.push(null);
    const rows: (string | null)[][] = [];
    for (let i = 0; i < cells.length; i += 7) rows.push(cells.slice(i, i + 7));
    return rows;
  }, [monthISO]);
  const monthHasPeriodDays = period ? monthEndISO(monthISO) >= period.starts_on && monthISO <= period.ends_on : false;

  const fair = useMemo(() => fairness(days ?? [], techs.map((t) => t.empno), holidaySet), [days, techs, holidaySet]);
  const filled = days?.length ?? 0;
  const periodDays = period ? Math.round((parseISO(period.ends_on).getTime() - parseISO(period.starts_on).getTime()) / 86400000) + 1 : 0;
  const noBackup = (days ?? []).filter((d) => !d.backup_empno).length;

  if (periodsLoading) return <Card className="p-6"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></Card>;

  return (
    <div className="space-y-4">
      {/* ── Period bar ─────────────────────────────────────────────────── */}
      <Card className="p-4">
        <div className="flex flex-wrap items-center gap-2">
          <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-slate-100"><Moon className="h-4.5 w-4.5 text-slate-600" /></span>
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-sm font-bold text-slate-900">
              On-Call Schedule
              <InfoTip label="How the on-call schedule works">Service techs only: one primary and one backup per day. An on-call day runs from office open to the next office open.</InfoTip>
            </div>
          </div>
          <div className="ml-auto flex flex-wrap items-center gap-2">
            {periods && periods.length > 0 && (
              <select
                value={period?.id ?? ""}
                onChange={(e) => setPeriodId(e.target.value)}
                className="h-9 rounded-lg border border-slate-200 bg-white px-2.5 text-[13px] font-semibold text-slate-800"
              >
                {periods.map((p) => (
                  <option key={p.id} value={p.id}>{p.label ?? periodLabel(p.starts_on, p.ends_on)}{p.status === "draft" ? " · draft" : ""}</option>
                ))}
              </select>
            )}
            {canBuild && (
              <Button size="sm" variant="secondary" onClick={() => setNewOpen(true)}><Plus className="mr-1 h-3.5 w-3.5" /> New period</Button>
            )}
            {canBuild && period && (
              <Button size="sm" variant="secondary" loading={update.isPending}
                title="Extend this period by one month"
                onClick={() => update.mutate({ id: period.id, ends_on: monthEndISO(addMonthsISO(period.ends_on, 1)), label: periodLabel(period.starts_on, monthEndISO(addMonthsISO(period.ends_on, 1))) })}>
                Add a month
              </Button>
            )}
            {period && period.status === "published" && (
              <>
                <Button size="sm" variant="secondary" onClick={() => window.open(`/team/oncall/print?period=${period.id}`, "_blank", "noopener")} title="The sheet CEAS gets — one month per page; Print → Save as PDF">
                  <Printer className="mr-1 h-3.5 w-3.5" /> Print for CEAS
                </Button>
                {canBuild && (
                  <Button size="sm" variant={changed.length > 0 ? "primary" : "secondary"} onClick={() => setCeasOpen(true)}>
                    <Mail className="mr-1 h-3.5 w-3.5" /> Send to CEAS
                  </Button>
                )}
              </>
            )}
            {canBuild && period && period.status === "draft" && !confirmPublish && (
              <Button size="sm" onClick={() => setConfirmPublish(true)}><Send className="mr-1 h-3.5 w-3.5" /> Publish</Button>
            )}
          </div>
        </div>
        {period && (
          <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500">
            <span className="font-semibold text-slate-700">{period.label ?? periodLabel(period.starts_on, period.ends_on)}</span>
            <span>{filled} of {periodDays} days filled{noBackup > 0 ? ` · ${noBackup} without a backup` : ""}</span>
            {period.status === "published"
              ? <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-px font-semibold text-emerald-700">Published {period.published_at ? fmtShort(period.published_at.slice(0, 10)) : ""}{period.published_by ? ` · ${period.published_by.split("@")[0]}` : ""} — every tech sees it</span>
              : <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-px font-semibold text-amber-800">Draft — only the office sees it</span>}
            {period.status === "published" && (
              !period.sent_to_ceas_at
                ? <span className="inline-flex items-center gap-1 rounded-full bg-red-50 px-2 py-px font-semibold text-red-800 ring-1 ring-red-200/70"><Mail className="h-3 w-3" /> Not yet sent to CEAS</span>
                : changed.length > 0
                  ? <span className="inline-flex items-center gap-1 rounded-full bg-red-50 px-2 py-px font-semibold text-red-800 ring-1 ring-red-200/70" title={changed.map((d) => fmtShort(d.day)).join(", ")}><Mail className="h-3 w-3" /> Changed since last sent · {changed.length} day{changed.length === 1 ? "" : "s"}{changed.length <= 4 ? ` (${changed.map((d) => fmtShort(d.day)).join(", ")})` : ""}</span>
                  : <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-px font-semibold text-slate-600"><Mail className="h-3 w-3" /> Sent to CEAS {fmtShort(period.sent_to_ceas_at.slice(0, 10))}{period.sent_to_ceas_by ? ` · ${period.sent_to_ceas_by.split("@")[0]}` : ""}</span>
            )}
            {canBuild && !confirmDelete && (
              <button type="button" onClick={() => setConfirmDelete(true)} className="ml-auto text-slate-400 hover:text-red-600">Delete period</button>
            )}
          </div>
        )}
        {confirmPublish && period && (
          <div className="mt-3 flex flex-wrap items-center gap-2 rounded-xl border border-brand-200 bg-brand-50 px-3 py-2 text-[13px] text-slate-800">
            <span>Publish <strong>{period.label ?? periodLabel(period.starts_on, period.ends_on)}</strong>? Every service tech sees it on their phone from now on{noBackup > 0 ? ` (${noBackup} day${noBackup === 1 ? "" : "s"} still without a backup — allowed)` : ""}. You can still change days afterwards.</span>
            <span className="ml-auto flex gap-2">
              <Button size="sm" variant="ghost" onClick={() => setConfirmPublish(false)}>Not yet</Button>
              <Button size="sm" loading={publish.isPending} onClick={() => publish.mutate(period.id, { onSuccess: () => setConfirmPublish(false) })}>Publish</Button>
            </span>
          </div>
        )}
        {confirmDelete && period && (
          <div className="mt-3 flex flex-wrap items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-[13px] text-slate-800">
            <span>Delete <strong>{period.label ?? periodLabel(period.starts_on, period.ends_on)}</strong> and its {filled} filled day{filled === 1 ? "" : "s"}? This cannot be undone.</span>
            <span className="ml-auto flex gap-2">
              <Button size="sm" variant="ghost" onClick={() => setConfirmDelete(false)}>Keep it</Button>
              <Button size="sm" variant="danger" loading={del.isPending} onClick={() => del.mutate(period.id, { onSuccess: () => { setConfirmDelete(false); setPeriodId(null); } })}>Delete</Button>
            </span>
          </div>
        )}
      </Card>

      {/* ── ONCALL-SCHED-01b: swap requests from the phone ─────────── */}
      {canEdit && <SwapRequestsCard nameOf={nameOf} techs={techs.map((t) => ({ empno: t.empno, name: techFirstName(t) }))} onOpenDay={(d) => { setPeriodId(null); setMonth(monthStartISO(d)); setEditing(d); }} />}
      {period && gapCount > 0 && (
        <div className="flex flex-wrap items-center gap-2 rounded-2xl border border-red-200 bg-red-50 px-4 py-2.5 text-[13px] text-red-900">
          <AlertTriangle className="h-4 w-4 text-red-600" />
          <span><strong>Coverage gap</strong> — {gapCount} on-call day{gapCount === 1 ? "" : "s"} where the tech has approved time off. Open the day and use Swap in…</span>
          <span className="ml-auto flex flex-wrap gap-1.5">
            {(gaps ?? []).filter((g) => g.status === "approved").slice(0, 6).map((g) => (
              <button key={`${g.day}-${g.role}`} type="button" onClick={() => { setMonth(monthStartISO(g.day)); setEditing(g.day); }}
                className="rounded-full bg-white px-2.5 py-0.5 text-[12px] font-semibold text-red-800 ring-1 ring-red-200 hover:bg-red-100">
                {fmtShort(g.day)} · {g.tech_name}
              </button>
            ))}
          </span>
        </div>
      )}

      {!period ? (
        <Card className="p-8">
          <EmptyState
            icon={CalendarDays}
            title="No on-call period yet"
            hint={canBuild ? "Start one — the next three months is one click, or any dates you like." : "The service manager builds the schedule here; once it is published it shows on every phone."}
          />
          {canBuild && (
            <div className="mt-4 flex justify-center"><Button onClick={() => setNewOpen(true)}><Plus className="mr-1 h-4 w-4" /> New period</Button></div>
          )}
        </Card>
      ) : (
        <>
          {/* ── Tools ─────────────────────────────────────────────────── */}
          {canEdit && (
            <div className="flex flex-wrap items-center gap-2 rounded-2xl border border-hairline bg-white px-3 py-2 shadow-sm">
              <span className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-slate-700">
                <Paintbrush className="h-3.5 w-3.5 text-slate-400" /> Paint
                <InfoTip label="How painting works">Pick a primary and a backup, then click a day or drag across days to paint the pair. Or tap any day to set it by hand.</InfoTip>
              </span>
              <select
                value={paint?.primary ?? ""}
                onChange={(e) => {
                  const v = e.target.value || null;
                  setPaint(v ? { primary: v, backup: paint?.backup && paint.backup !== v ? paint.backup : null } : null);
                }}
                className="h-8 rounded-lg border border-blue-200 bg-blue-50 px-2 text-[13px] font-semibold text-blue-800"
              >
                <option value="">Pick a primary…</option>
                {techs.map((t) => <option key={t.empno} value={t.empno}>{techFirstName(t)}</option>)}
              </select>
              <select
                value={paint?.backup ?? ""}
                disabled={!paint}
                onChange={(e) => setPaint(paint ? { ...paint, backup: e.target.value || null } : null)}
                className="h-8 rounded-lg border border-orange-200 bg-orange-50 px-2 text-[13px] font-semibold text-orange-800 disabled:opacity-50"
              >
                <option value="">No backup</option>
                {techs.filter((t) => t.empno !== paint?.primary).map((t) => <option key={t.empno} value={t.empno}>{techFirstName(t)}</option>)}
              </select>
              {paint && (
                <span className="flex items-center gap-2 text-[12px] text-slate-500">
                  Click a day or drag across days to paint <PairChip name={nameOf(paint.primary)} role="primary" small />{paint.backup ? <PairChip name={nameOf(paint.backup)} role="backup" small /> : <PairChip name="no backup" role="none" small />}
                  <button type="button" onClick={() => setPaint(null)} className="rounded-full p-0.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700" title="Stop painting"><X className="h-3.5 w-3.5" /></button>
                </span>
              )}
              <span className="ml-auto flex items-center gap-2">
                {canBuild && <Button size="sm" variant="secondary" onClick={() => setImportOpen(true)} title="A photo, a screenshot or a PDF of the on-call sheet — read into the calendar, reviewed before anything lands"><Upload className="mr-1 h-3.5 w-3.5" /> Upload a sheet…</Button>}
                {canBuild && <Button size="sm" variant="secondary" onClick={() => setFillOpen(true)}><Wand2 className="mr-1 h-3.5 w-3.5" /> Fill empty days…</Button>}
                {canSettings && <Button size="sm" variant="ghost" onClick={() => setHolOpen(true)} title="Office-closed dates (shared with payroll)"><CalendarOff className="mr-1 h-3.5 w-3.5" /> Closed dates</Button>}
              </span>
            </div>
          )}

          {/* ── Calendar ──────────────────────────────────────────────── */}
          <Card className="overflow-hidden p-0">
            <div className="flex items-center justify-center gap-3 border-b border-hairline px-4 py-2.5">
              <button type="button" onClick={() => setMonth(addMonthsISO(monthISO, -1))} className="rounded-lg p-1 text-slate-500 hover:bg-slate-100" title="Previous month"><ChevronLeft className="h-4 w-4" /></button>
              <div className="min-w-[12rem] text-center text-[15px] font-bold text-slate-900">{fmtMonth(monthISO)}</div>
              <button type="button" onClick={() => setMonth(addMonthsISO(monthISO, 1))} className="rounded-lg p-1 text-slate-500 hover:bg-slate-100" title="Next month"><ChevronRight className="h-4 w-4" /></button>
              {!monthHasPeriodDays && <span className="text-xs text-slate-400">outside {period.label ?? periodLabel(period.starts_on, period.ends_on)}</span>}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full table-fixed border-collapse select-none">
                <thead>
                  <tr>{WEEKDAYS.map((w) => <th key={w} className="border-b border-hairline bg-slate-50 py-1.5 text-center text-[10.5px] font-bold uppercase tracking-wider text-slate-500">{w}</th>)}</tr>
                </thead>
                <tbody>
                  {grid.map((row, ri) => (
                    <tr key={ri}>
                      {row.map((iso, ci) => {
                        if (!iso) return <td key={ci} className="h-[5.5rem] border-b border-r border-hairline bg-slate-50/60 last:border-r-0" />;
                        const d = dayMap.get(iso);
                        const inside = inPeriod(iso);
                        const isToday = iso === today;
                        const hol = holidaySet.has(iso);
                        const painting = dragSet.has(iso);
                        const dayGaps = gapMap.get(iso) ?? [];
                        const hardGap = dayGaps.some((g) => g.status === "approved");
                        const softGap = !hardGap && dayGaps.length > 0;
                        return (
                          <td
                            key={iso}
                            onMouseDown={(e) => { if (e.button === 0) { e.preventDefault(); onDayMouseDown(iso); } }}
                            onMouseEnter={() => onDayMouseEnter(iso)}
                            className={cn(
                              "h-[5.5rem] border-b border-r border-hairline p-1.5 align-top last:border-r-0",
                              !inside && "bg-slate-50/60",
                              inside && canEdit && "cursor-pointer hover:bg-slate-50",
                              hol && inside && "bg-amber-50/60",
                              hardGap && "bg-red-50",
                              softGap && "bg-amber-50",
                              painting && "bg-blue-50 ring-2 ring-inset ring-blue-300",
                              isToday && "shadow-[inset_0_0_0_2px_theme(colors.brand.500)]",
                            )}
                          >
                            <div className="flex items-center gap-1">
                              <span className={cn("text-[11.5px] font-semibold tabular-nums", inside ? "text-slate-500" : "text-slate-300", isToday && "text-brand-700")}>{Number(iso.slice(8, 10))}</span>
                              {isToday && <span className="text-[9px] font-extrabold uppercase tracking-wider text-brand-700">today</span>}
                              {hol && inside && <span className="ml-auto rounded-full bg-amber-100 px-1.5 text-[9.5px] font-bold uppercase tracking-wide text-amber-800">closed</span>}
                            </div>
                            {inside && (
                              <div className="mt-1 flex flex-col items-start gap-1">
                                {d ? (
                                  <>
                                    <PairChip name={nameOf(d.primary_empno)} role="primary" small />
                                    {d.backup_empno ? <PairChip name={nameOf(d.backup_empno)} role="backup" small /> : <PairChip name="no backup" role="none" small />}
                                    {dayGaps.map((g) => (
                                      <span key={`${g.day}-${g.role}`} className={cn("inline-flex max-w-full items-center gap-1 rounded-full px-1.5 py-px text-[10.5px] font-bold", g.status === "approved" ? "bg-red-100 text-red-800" : "bg-amber-100 text-amber-800")} title={`${g.tech_name} (${g.role}) — ${availOutLabel({ kind: g.kind as AvailEvent["kind"] })}${g.status === "requested" ? " — request waiting" : ""}`}>
                                        <AlertTriangle className="h-3 w-3 shrink-0" /><span className="truncate">{g.status === "approved" ? `${g.tech_name} off · no cover` : `${g.tech_name} asked off`}</span>
                                      </span>
                                    ))}
                                    {d.note && <span className="truncate text-[10.5px] text-slate-500" title={d.note}>{d.note}</span>}
                                  </>
                                ) : (
                                  <span className="text-[11px] text-slate-300">—</span>
                                )}
                              </div>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          {/* ── Fairness strip — counts days for the whole period ─────── */}
          <div>
            <div className="mb-1.5 flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-slate-500">
              <Sparkles className="h-3.5 w-3.5" /> Days each tech carries · {period.label ?? periodLabel(period.starts_on, period.ends_on)}
              <InfoTip label="How the days are counted">Weekend means Friday, Saturday and Sunday. The strip only counts days; the room decides what is fair.</InfoTip>
            </div>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-7">
              {fair.map((f) => (
                <div key={f.empno} className="rounded-xl border border-hairline bg-white px-3 py-2 shadow-sm">
                  <div className="text-[13px] font-bold text-slate-900">{nameOf(f.empno)}</div>
                  <div className="text-[11.5px] text-slate-500">
                    <span className="font-semibold text-blue-700">{f.primary}</span> primary · <span className="font-semibold text-orange-700">{f.backup}</span> backup
                    <span className="block">{f.weekend} weekend{f.holidays > 0 ? ` · ${f.holidays} closed-day` : ""}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {/* ── Day editor ─────────────────────────────────────────────────── */}
      {editing && period && (
        <DayEditor
          iso={editing}
          current={dayMap.get(editing) ?? null}
          block={blocksOf(days ?? []).find((b) => b.start <= editing && b.end >= editing) ?? null}
          gaps={gapMap.get(editing) ?? []}
          fair={fair}
          techs={techs.map((t) => ({ empno: t.empno, name: techFirstName(t) }))}
          canEdit={canEdit}
          onClose={() => setEditing(null)}
          onSave={(v, wholeBlock) => {
            const block = wholeBlock ? blocksOf(days ?? []).find((b) => b.start <= editing && b.end >= editing) : null;
            const targets: string[] = [];
            if (block) for (let d = block.start; d <= block.end; d = addDaysISO(d, 1)) targets.push(d);
            else targets.push(editing);
            upsert.mutate(targets.map((day) => ({ day, ...v, note: day === editing ? v.note : (dayMap.get(day)?.note ?? null), source: "edit" as const })), { onSuccess: () => setEditing(null) });
          }}
          onClear={() => clear.mutate([editing], { onSuccess: () => setEditing(null) })}
          saving={upsert.isPending || clear.isPending}
        />
      )}

      {newOpen && (
        <NewPeriodModal
          periods={periods ?? []}
          onClose={() => setNewOpen(false)}
          onCreated={(id) => { setNewOpen(false); setPeriodId(id); setMonth(null); }}
        />
      )}

      {fillOpen && period && (
        <FillModal
          period={period}
          monthISO={monthISO}
          techs={techs.map((t) => ({ empno: t.empno, name: techFirstName(t) }))}
          existing={dayMap}
          onClose={() => setFillOpen(false)}
          onApply={(rows) => upsert.mutate(rows, { onSuccess: () => setFillOpen(false) })}
          saving={upsert.isPending}
        />
      )}

      {importOpen && period && (
        <ImportSheetModal
          period={period}
          techs={techs.map((t) => ({ empno: t.empno, name: techFirstName(t), full: `${(t.firstname ?? "").trim()} ${(t.lastname ?? "").trim()}`.trim() || t.empname || t.empno }))}
          existing={dayMap}
          onClose={() => setImportOpen(false)}
          onApply={(rows) => upsert.mutate(rows, { onSuccess: () => setImportOpen(false) })}
          saving={upsert.isPending}
        />
      )}

      {holOpen && <ClosedDatesModal onClose={() => setHolOpen(false)} />}

      {ceasOpen && period && (
        <SendToCeasModal
          period={period}
          days={days ?? []}
          changed={changed}
          nameOf={nameOf}
          canSettings={canSettings}
          onClose={() => setCeasOpen(false)}
          onMarkSent={() => markSent.mutate(period.id)}
          marking={markSent.isPending}
        />
      )}
    </div>
  );
}

// ── Day editor ──────────────────────────────────────────────────────────────
function DayEditor({ iso, current, block, gaps, fair, techs, canEdit, onClose, onSave, onClear, saving }: {
  iso: string;
  current: OncallDay | null;
  block: { start: string; end: string; days: number } | null;
  gaps: OncallGap[];
  fair: { empno: string; primary: number; backup: number }[];
  techs: { empno: string; name: string }[];
  canEdit: boolean;
  onClose: () => void;
  onSave: (v: { primary_empno: string; backup_empno: string | null; note: string | null }, wholeBlock: boolean) => void;
  onClear: () => void;
  saving: boolean;
}) {
  const [primary, setPrimary] = useState(current?.primary_empno ?? "");
  const [backup, setBackup] = useState(current?.backup_empno ?? "");
  const [note, setNote] = useState(current?.note ?? "");
  const [wholeBlock, setWholeBlock] = useState(false);
  // The day's row can land after the editor opened (a deep link) — take it then.
  useEffect(() => {
    setPrimary(current?.primary_empno ?? ""); setBackup(current?.backup_empno ?? ""); setNote(current?.note ?? "");
  }, [current?.day, current?.updated_at]); // eslint-disable-line react-hooks/exhaustive-deps
  const weekend = isWeekendISO(iso);
  // ONCALL-SCHED-01b: who is out that day (any kind, not denied) → never suggested; the
  // rest ranked fewest on-call days first, this day's backup first.
  const out = useTechAvailability(iso).data ?? [];
  const outSet = new Set(out.filter((e) => e.status !== "denied" && ["sick", "vacation", "personal", "other", "training"].includes(e.kind)).map((e) => e.empno));
  const gapRole = gaps.find((g) => g.status === "approved")?.role ?? gaps[0]?.role ?? null;
  const load = new Map(fair.map((f) => [f.empno, f.primary + f.backup]));
  const suggestions = techs
    .filter((t) => !outSet.has(t.empno) && t.empno !== (gapRole === "backup" ? current?.backup_empno : current?.primary_empno))
    .sort((a, b) => {
      const ab = a.empno === current?.backup_empno ? -1 : 0, bb = b.empno === current?.backup_empno ? -1 : 0;
      if (ab !== bb) return ab - bb;
      return (load.get(a.empno) ?? 0) - (load.get(b.empno) ?? 0);
    })
    .slice(0, 5);
  const takeCover = (empno: string) => {
    if (gapRole === "backup") { setBackup(empno); return; }
    // cover the primary: the day's backup steps up if they are the pick; the old backup stays unless it is the same person
    setPrimary(empno);
    if (backup === empno) setBackup("");
  };
  const onFormKeys = useEntryFormKeys();
  return (
    <Modal open onClose={onClose} size="sm"
      title={
        <span className="flex items-center gap-2">
          {fmtWeekday(iso)}
          <InfoTip label="About this day">{weekend ? "A weekend day. Friday, Saturday and Sunday usually share a pair." : "On call from office close that night until the office opens the next morning."}</InfoTip>
        </span>
      }>
      <div className="space-y-3" data-entry-form onKeyDown={onFormKeys}>
        {gaps.length > 0 && (
          <div className={cn("rounded-xl px-3 py-2 text-[12.5px]", gaps.some((g) => g.status === "approved") ? "bg-red-50 text-red-900" : "bg-amber-50 text-amber-900")}>
            {gaps.map((g) => (
              <div key={`${g.day}-${g.role}`} className="flex items-start gap-1.5">
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                <span><strong>{g.tech_name}</strong> ({g.role}) is {g.status === "approved" ? "off" : "asking off"} — {availOutLabel({ kind: g.kind as AvailEvent["kind"] })}{g.title ? ` · "${g.title}"` : ""}{g.status === "requested" ? " · request waiting on the office" : ""}.</span>
              </div>
            ))}
          </div>
        )}
        {canEdit && suggestions.length > 0 && (gaps.length > 0 || !current) && (
          <div>
            <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-slate-500">
              {gaps.length > 0 ? `Swap in as ${gapRole ?? "primary"}` : "Suggested"}
              <InfoTip label="How suggestions are picked">Leaves out anyone who is off that day. The tech with the fewest on-call days comes first.</InfoTip>
            </div>
            <div className="mt-1 flex flex-wrap gap-1.5">
              {suggestions.map((t) => (
                <button key={t.empno} type="button" onClick={() => takeCover(t.empno)}
                  className={cn("inline-flex items-center gap-1.5 rounded-full border px-2 py-1 text-[12.5px] font-semibold", (gapRole === "backup" ? backup : primary) === t.empno ? "border-brand-300 bg-brand-50 text-brand-800" : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50")}>
                  <TechAvatar empno={t.empno} name={t.name} size="xs" /> {t.name}
                  <span className="text-[10.5px] font-normal text-slate-400">{load.get(t.empno) ?? 0} d{t.empno === current?.backup_empno ? " · backup today" : ""}</span>
                </button>
              ))}
            </div>
          </div>
        )}
        <label className="block">
          <span className="text-[11px] font-bold uppercase tracking-wider text-blue-700">Primary</span>
          <select value={primary} onChange={(e) => { setPrimary(e.target.value); if (backup === e.target.value) setBackup(""); }} disabled={!canEdit}
            className="mt-1 h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm">
            <option value="">— pick a tech —</option>
            {techs.map((t) => <option key={t.empno} value={t.empno}>{t.name}</option>)}
          </select>
        </label>
        <label className="block">
          <span className="text-[11px] font-bold uppercase tracking-wider text-orange-700">Backup</span>
          <select value={backup} onChange={(e) => setBackup(e.target.value)} disabled={!canEdit}
            className="mt-1 h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm">
            <option value="">No backup</option>
            {techs.filter((t) => t.empno !== primary).map((t) => <option key={t.empno} value={t.empno}>{t.name}</option>)}
          </select>
        </label>
        <label className="block">
          <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Note</span>
          <input value={note} onChange={(e) => setNote(e.target.value)} disabled={!canEdit} placeholder="e.g. Brent has the truck"
            className="mt-1 h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm" />
        </label>
        {block && block.days > 1 && canEdit && (
          <label className="flex items-center gap-2 text-[13px] text-slate-700">
            <input type="checkbox" checked={wholeBlock} onChange={(e) => setWholeBlock(e.target.checked)} />
            Apply to the whole block · {blockLabel({ ...block, primary_empno: "", backup_empno: null })} ({block.days} days)
          </label>
        )}
        {current?.updated_by && (
          <div className="text-[11px] text-slate-400">Last changed by {current.updated_by.split("@")[0]} · {fmtShort(current.updated_at.slice(0, 10))}</div>
        )}
        <div className="flex items-center gap-2 pt-1">
          {current && canEdit && (
            <Button variant="ghost" size="sm" onClick={onClear} disabled={saving} className="text-red-600 hover:bg-red-50"><Trash2 className="mr-1 h-3.5 w-3.5" /> Clear day</Button>
          )}
          <span className="ml-auto flex gap-2">
            <Button variant="secondary" size="sm" onClick={onClose}>Cancel</Button>
            {canEdit && <Button size="sm" loading={saving} disabled={!primary} onClick={() => onSave({ primary_empno: primary, backup_empno: backup || null, note: note.trim() || null }, wholeBlock)}>Save</Button>}
          </span>
        </div>
      </div>
    </Modal>
  );
}

// ── New period ──────────────────────────────────────────────────────────────
function NewPeriodModal({ periods, onClose, onCreated }: { periods: OncallPeriod[]; onClose: () => void; onCreated: (id: string) => void }) {
  const create = useCreateOncallPeriod();
  const today = isoDate(new Date());
  // Default: the day after the latest period, else the first of next month, for three months.
  const latestEnd = periods.reduce<string | null>((m, p) => (!m || p.ends_on > m ? p.ends_on : m), null);
  const defaultStart = latestEnd && latestEnd >= today ? addDaysISO(latestEnd, 1) : monthStartISO(addMonthsISO(today, 1));
  const [start, setStart] = useState(defaultStart);
  const [end, setEnd] = useState(monthEndISO(addMonthsISO(defaultStart, 2)));
  const [label, setLabel] = useState("");
  const overlap = periods.find((p) => p.starts_on <= end && p.ends_on >= start);
  const bad = !start || !end || end < start;
  const preset = (months: number) => { setStart(defaultStart); setEnd(monthEndISO(addMonthsISO(defaultStart, months - 1))); };
  const onFormKeys = useEntryFormKeys();
  return (
    <Modal open onClose={onClose} size="sm"
      title={
        <span className="flex items-center gap-2">
          New On-Call Period
          <InfoTip label="About periods">Any stretch you like; three months is the usual. You can add a month later.</InfoTip>
        </span>
      }>
      <div className="space-y-3" data-entry-form onKeyDown={onFormKeys}>
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="secondary" onClick={() => preset(3)}>Next 3 months</Button>
          <Button size="sm" variant="secondary" onClick={() => preset(1)}>Next month</Button>
          <Button size="sm" variant="secondary" onClick={() => preset(6)}>Next 6 months</Button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Starts</span>
            <input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="mt-1 h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm" />
          </label>
          <label className="block">
            <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Ends</span>
            <input type="date" value={end} min={start} onChange={(e) => setEnd(e.target.value)} className="mt-1 h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm" />
          </label>
        </div>
        <label className="block">
          <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Name</span>
          <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder={!bad ? periodLabel(start, end) : ""} className="mt-1 h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm" />
        </label>
        {overlap && <div className="rounded-lg bg-amber-50 px-3 py-2 text-[12.5px] text-amber-900">Overlaps {overlap.label ?? periodLabel(overlap.starts_on, overlap.ends_on)} — periods cannot share a day. Use Add a month on that one instead.</div>}
        {create.isError && <div className="rounded-lg bg-red-50 px-3 py-2 text-[12.5px] text-red-800">{(create.error as Error).message}</div>}
        <div className="flex justify-end gap-2 pt-1">
          <Button variant="secondary" size="sm" onClick={onClose}>Cancel</Button>
          <Button size="sm" loading={create.isPending} disabled={bad || !!overlap}
            onClick={() => create.mutate({ starts_on: start, ends_on: end, label: label.trim() || null }, { onSuccess: (p) => onCreated(p.id) })}>
            Create
          </Button>
        </div>
      </div>
    </Modal>
  );
}

// ── Fill empty days (the rotation helper) ───────────────────────────────────
type Pattern = "blocks" | "weeks" | "days";
const PATTERNS: { key: Pattern; label: string; hint?: string }[] = [
  { key: "blocks", label: "Mon–Tue · Wed–Thu · Fri–Sun", hint: "The way the sheet runs today" },
  { key: "weeks", label: "Whole weeks (Mon–Sun)" },
  { key: "days", label: "Every day" },
];
/** Which block a date belongs to, for a pattern — consecutive days in one block share a pair. */
function blockKey(iso: string, pattern: Pattern): string {
  const d = parseISO(iso);
  const w = d.getDay(); // 0 Sun … 6 Sat
  if (pattern === "days") return iso;
  // Week anchor = Monday
  const mon = new Date(d); mon.setDate(d.getDate() - ((w + 6) % 7));
  const monISO = isoDate(mon);
  if (pattern === "weeks") return monISO;
  if (w === 1 || w === 2) return `${monISO}:a`;
  if (w === 3 || w === 4) return `${monISO}:b`;
  return `${monISO}:c`; // Fri, Sat, Sun
}

function FillModal({ period, monthISO, techs, existing, onClose, onApply, saving }: {
  period: OncallPeriod;
  monthISO: string;
  techs: { empno: string; name: string }[];
  existing: Map<string, OncallDay>;
  onClose: () => void;
  onApply: (rows: OncallDayInput[]) => void;
  saving: boolean;
}) {
  const [pattern, setPattern] = useState<Pattern>("blocks");
  const [scope, setScope] = useState<"month" | "period">("period");
  const [order, setOrder] = useState<string[]>(techs.map((t) => t.empno));
  const [withBackup, setWithBackup] = useState(true);
  const nameOf = (e: string) => techs.find((t) => t.empno === e)?.name ?? e;
  const move = (e: string, dir: -1 | 1) => setOrder((o) => { const i = o.indexOf(e); const j = i + dir; if (i < 0 || j < 0 || j >= o.length) return o; const n = [...o]; [n[i], n[j]] = [n[j], n[i]]; return n; });
  const toggle = (e: string) => setOrder((o) => o.includes(e) ? o.filter((x) => x !== e) : [...o, e]);

  const rows = useMemo<OncallDayInput[]>(() => {
    if (order.length === 0) return [];
    const from = scope === "month" ? (monthISO > period.starts_on ? monthISO : period.starts_on) : period.starts_on;
    const to = scope === "month" ? (monthEndISO(monthISO) < period.ends_on ? monthEndISO(monthISO) : period.ends_on) : period.ends_on;
    if (from > to) return [];
    const out: OncallDayInput[] = [];
    const pairByBlock = new Map<string, { p: string; b: string | null }>();
    let i = 0;
    for (let d = from; d <= to; d = addDaysISO(d, 1)) {
      if (existing.has(d)) continue; // never overwrite a filled day
      const k = blockKey(d, pattern);
      let pair = pairByBlock.get(k);
      if (!pair) {
        const p = order[i % order.length];
        const b = withBackup && order.length > 1 ? order[(i + 1) % order.length] : null;
        pair = { p, b };
        pairByBlock.set(k, pair);
        i += 1;
      }
      out.push({ day: d, primary_empno: pair.p, backup_empno: pair.b, source: "fill" });
    }
    return out;
  }, [order, scope, monthISO, period, existing, pattern, withBackup]);

  const onFormKeys = useEntryFormKeys();
  return (
    <Modal open onClose={onClose} size="md"
      title={
        <span className="flex items-center gap-2">
          Fill Empty Days
          <InfoTip label="How the fill works">Lays a rotation over the days that are still empty. It never touches a day you have already set, and you can adjust anything afterwards by hand.</InfoTip>
        </span>
      }>
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-3" data-entry-form onKeyDown={onFormKeys}>
          <div>
            <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Pattern</div>
            <div className="mt-1 space-y-1.5">
              {PATTERNS.map((p) => (
                <label key={p.key} className={cn("flex cursor-pointer items-start gap-2 rounded-lg border px-3 py-2", pattern === p.key ? "border-brand-300 bg-brand-50" : "border-slate-200")}>
                  <input type="radio" name="pattern" checked={pattern === p.key} onChange={() => setPattern(p.key)} className="mt-1" />
                  <span><span className="block text-[13px] font-semibold text-slate-800">{p.label}</span>{p.hint && <span className="block text-[11.5px] text-slate-500">{p.hint}</span>}</span>
                </label>
              ))}
            </div>
          </div>
          <div>
            <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Where</div>
            <div className="mt-1 flex gap-2">
              <button type="button" onClick={() => setScope("period")} className={cn("rounded-full px-3 py-1 text-[12.5px] font-semibold", scope === "period" ? "bg-brand-700 text-white" : "bg-slate-100 text-slate-600")}>Whole period</button>
              <button type="button" onClick={() => setScope("month")} className={cn("rounded-full px-3 py-1 text-[12.5px] font-semibold", scope === "month" ? "bg-brand-700 text-white" : "bg-slate-100 text-slate-600")}>{fmtMonth(monthISO)} only</button>
            </div>
          </div>
          <label className="flex items-center gap-2 text-[13px] text-slate-700">
            <input type="checkbox" checked={withBackup} onChange={(e) => setWithBackup(e.target.checked)} /> Backup = the next tech in the order
          </label>
        </div>
        <div>
          <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-slate-500">
            Rotation order
            <InfoTip label="About the rotation order">Untick a tech to leave them out of this fill, such as a new hire or a lead who sits out.</InfoTip>
          </div>
          <ul className="mt-1 space-y-1">
            {techs.map((t) => {
              const on = order.includes(t.empno);
              const idx = order.indexOf(t.empno);
              return (
                <li key={t.empno} className={cn("flex items-center gap-2 rounded-lg border px-2.5 py-1.5", on ? "border-slate-200 bg-white" : "border-dashed border-slate-200 bg-slate-50 text-slate-400")}>
                  <input type="checkbox" checked={on} onChange={() => toggle(t.empno)} />
                  <span className="w-5 text-[11px] font-bold tabular-nums text-slate-400">{on ? idx + 1 : ""}</span>
                  <span className="flex-1 text-[13px] font-semibold">{t.name}</span>
                  {on && (
                    <span className="flex gap-0.5">
                      <button type="button" onClick={() => move(t.empno, -1)} className="rounded p-0.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700" title="Earlier">↑</button>
                      <button type="button" onClick={() => move(t.empno, 1)} className="rounded p-0.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700" title="Later">↓</button>
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      </div>
      <div className="mt-4 flex items-center gap-2 border-t border-hairline pt-3">
        <span className="text-[12.5px] text-slate-600">
          {rows.length === 0 ? "Nothing to fill — every day in range is already set." : <>Fills <strong>{rows.length}</strong> empty day{rows.length === 1 ? "" : "s"}, starting with <strong>{nameOf(rows[0].primary_empno)}</strong>{rows[0].backup_empno ? ` / ${nameOf(rows[0].backup_empno)}` : ""} on {fmtWeekday(rows[0].day)}.</>}
        </span>
        <span className="ml-auto flex gap-2">
          <Button variant="secondary" size="sm" onClick={onClose}>Cancel</Button>
          <Button size="sm" loading={saving} disabled={rows.length === 0} onClick={() => onApply(rows)}>Fill {rows.length || ""}</Button>
        </span>
      </div>
    </Modal>
  );
}

// ── Office-closed dates (shared with payroll's holiday tint) ────────────────
function ClosedDatesModal({ onClose }: { onClose: () => void }) {
  const stored = useOrgSettingJson<string[]>("payroll_holidays", []).data ?? [];
  const save = useSaveOrgSetting();
  const [list, setList] = useState<string[] | null>(null);
  const cur = (list ?? stored).slice().sort();
  const [add, setAdd] = useState("");
  const dirty = list !== null && JSON.stringify(cur) !== JSON.stringify(stored.slice().sort());
  const onFormKeys = useEntryFormKeys();
  return (
    <Modal open onClose={onClose} size="sm"
      title={
        <span className="flex items-center gap-2">
          Office-Closed Dates
          <InfoTip label="Where closed dates show">Payroll tints these dates, and the on-call calendar marks them so the room sees them while filling.</InfoTip>
        </span>
      }>
      <div className="space-y-3" data-entry-form onKeyDown={onFormKeys}>
        <div className="flex gap-2">
          <input type="date" value={add} onChange={(e) => setAdd(e.target.value)} className="h-10 flex-1 rounded-lg border border-slate-200 bg-white px-3 text-sm" />
          <Button size="sm" variant="secondary" disabled={!add || cur.includes(add)} onClick={() => { setList([...cur, add]); setAdd(""); }}>Add</Button>
        </div>
        {cur.length === 0 ? (
          <p className="text-[12.5px] text-slate-400">No closed dates yet — add Thanksgiving, Christmas, New Year's…</p>
        ) : (
          <ul className="max-h-64 divide-y divide-hairline overflow-y-auto rounded-lg border border-hairline">
            {cur.map((d) => (
              <li key={d} className="flex items-center gap-2 px-3 py-1.5 text-[13px] text-slate-800">
                <span>{fmtWeekday(d)}, {d.slice(0, 4)}</span>
                <button type="button" onClick={() => setList(cur.filter((x) => x !== d))} className="ml-auto rounded p-0.5 text-slate-400 hover:bg-red-50 hover:text-red-600" title="Remove"><X className="h-3.5 w-3.5" /></button>
              </li>
            ))}
          </ul>
        )}
        <div className="flex justify-end gap-2 pt-1">
          <Button variant="secondary" size="sm" onClick={onClose}>Close</Button>
          <Button size="sm" loading={save.isPending} disabled={!dirty} onClick={() => save.mutate({ key: "payroll_holidays", value: cur }, { onSuccess: onClose })}>Save</Button>
        </div>
      </div>
    </Modal>
  );
}

// ── ONCALL-SCHED-01b — swap requests from the phone ─────────────────────────
function SwapRequestsCard({ nameOf, techs, onOpenDay }: {
  nameOf: (e: string | null | undefined) => string;
  techs: { empno: string; name: string }[];
  onOpenDay: (iso: string) => void;
}) {
  const { data: reqs } = useOncallSwapRequests("requested");
  const decide = useDecideOncallSwap();
  const [cover, setCover] = useState<Record<string, string>>({});
  const [denying, setDenying] = useState<string | null>(null);
  const [note, setNote] = useState("");
  if (!reqs || reqs.length === 0) return null;
  const span = (r: OncallSwapRequest) => (r.from_day === r.to_day ? fmtWeekday(r.from_day) : `${fmtShort(r.from_day)} – ${fmtShort(r.to_day)}`);
  return (
    <Card className="border-sky-200 bg-sky-50/40 p-4">
      <div className="flex items-center gap-2">
        <ArrowLeftRight className="h-4 w-4 text-sky-700" />
        <span className="text-sm font-bold text-slate-900">Swap Requests</span>
        <InfoTip label="What approving does">Approving puts the cover tech on those days, and the tech hears back in chat.</InfoTip>
        <span className="rounded-full bg-sky-600 px-1.5 py-0.5 text-[10px] font-bold text-white">{reqs.length}</span>
      </div>
      <ul className="mt-2.5 space-y-2">
        {reqs.map((r) => {
          const pick = cover[r.id] ?? r.with_empno ?? "";
          return (
            <li key={r.id} className="rounded-lg border border-sky-200/70 bg-white p-3">
              <div className="flex flex-wrap items-center gap-2.5">
                <TechAvatar empno={r.empno} name={nameOf(r.empno)} size="sm" />
                <span className="min-w-0">
                  <span className="block text-sm font-semibold text-slate-800">{nameOf(r.empno)} <span className="font-normal text-slate-500">wants out of on-call ({r.role})</span></span>
                  <button type="button" onClick={() => onOpenDay(r.from_day)} className="block text-xs text-brand-700 hover:underline">{span(r)}{r.note ? <span className="text-slate-400"> — &ldquo;{r.note}&rdquo;</span> : null}</button>
                </span>
                <span className="ml-auto flex flex-wrap items-center gap-2">
                  <select value={pick} onChange={(e) => setCover({ ...cover, [r.id]: e.target.value })}
                    className="h-8 rounded-lg border border-slate-200 bg-white px-2 text-[12.5px] font-semibold text-slate-800">
                    <option value="">Who covers…</option>
                    {techs.filter((t) => t.empno !== r.empno).map((t) => <option key={t.empno} value={t.empno}>{t.name}{t.empno === r.with_empno ? " (asked)" : ""}</option>)}
                  </select>
                  <Button size="sm" disabled={!pick || decide.isPending} loading={decide.isPending && decide.variables?.req.id === r.id}
                    onClick={() => decide.mutate({ req: r, decision: "approved", cover_empno: pick })}>
                    <Check className="mr-1 h-3.5 w-3.5" /> Approve
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => { setDenying(denying === r.id ? null : r.id); setNote(""); }}>Deny</Button>
                </span>
              </div>
              {denying === r.id && (
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Why (the tech sees this)" className="h-8 flex-1 rounded-lg border border-slate-200 bg-white px-2.5 text-[12.5px]" />
                  <Button size="sm" variant="danger" loading={decide.isPending} onClick={() => decide.mutate({ req: r, decision: "denied", note }, { onSuccess: () => setDenying(null) })}>Deny</Button>
                </div>
              )}
              {decide.isError && decide.variables?.req.id === r.id && <div className="mt-2 text-[12px] text-red-700">{(decide.error as Error).message}</div>}
            </li>
          );
        })}
      </ul>
    </Card>
  );
}

// ── ONCALL-SCHED-01c — Send to CEAS ─────────────────────────────────────────
// The same sheet, by email, on an explicit click every time. Vendor mail rides
// the comms gate like a subcontractor work order: while Pegasus is in testing
// it is recorded as HELD and the office prints/forwards the PDF instead.
function SendToCeasModal({ period, days, changed, nameOf, canSettings, onClose, onMarkSent, marking }: {
  period: OncallPeriod;
  days: OncallDay[];
  changed: OncallDay[];
  nameOf: (e: string | null | undefined) => string;
  canSettings: boolean;
  onClose: () => void;
  onMarkSent: () => void;
  marking: boolean;
}) {
  const stored = useOrgSettingJson<string>("oncall_ceas_email", "").data ?? "";
  const save = useSaveOrgSetting();
  const [to, setTo] = useState<string | null>(null);
  const addr = (to ?? stored).trim();
  const [withContacts, setWithContacts] = useState(false);
  const empnos = useMemo(() => Array.from(new Set(days.flatMap((d) => [d.primary_empno, d.backup_empno].filter(Boolean) as string[]))), [days]);
  const contacts = useOncallContacts(withContacts ? empnos : []).data ?? null;
  const [result, setResult] = useState<{ ok: boolean; text: string } | null>(null);
  const [sending, setSending] = useState(false);
  const send = async () => {
    setSending(true); setResult(null);
    try {
      if (canSettings && addr && addr !== stored) await save.mutateAsync({ key: "oncall_ceas_email", value: addr });
      const { data: u } = await supabase.auth.getUser();
      const html = renderOncallSheetHtml({ period, days, nameOf: (e) => nameOf(e), contacts: withContacts ? contacts : null, generatedBy: u?.user?.email ?? null });
      const subject = `Sedgwick Heating on-call schedule — ${period.label ?? periodLabel(period.starts_on, period.ends_on)}`;
      const { data: res, error } = await supabase.functions.invoke("send-email", { body: { to: [addr], subject, html, message_type: "oncall-sheet" } });
      if (error) throw error;
      const ok = !!(res as { ok?: boolean } | null)?.ok;
      if (ok) { onMarkSent(); setResult({ ok: true, text: `Sent to ${addr}. Marked as sent.` }); }
      else setResult({ ok: false, text: "Recorded as HELD — outbound email is in testing and sends for real once comms go live. Print the sheet (Save as PDF) and forward it yourself, then Mark as sent below." });
    } catch (e) {
      setResult({ ok: false, text: (e as Error).message || "Couldn't send — try again." });
    } finally { setSending(false); }
  };
  const onFormKeys = useEntryFormKeys();
  return (
    <Modal open onClose={onClose} size="md"
      title={
        <span className="flex items-center gap-2">
          Send to CEAS
          <InfoTip label="What gets sent">The same one-month-per-page sheet, by email. Nothing goes out unless you click Send.</InfoTip>
        </span>
      }>
      <div className="space-y-3" data-entry-form onKeyDown={onFormKeys}>
        <label className="block">
          <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">To (the answering service)</span>
          <input value={to ?? stored} onChange={(e) => setTo(e.target.value)} disabled={!canSettings && !!stored} placeholder="schedules@ceas.us" type="email"
            className="mt-1 h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm disabled:bg-slate-50" />
          {!stored && !canSettings && <span className="text-[11.5px] text-amber-700">No CEAS address on file yet — an admin sets it here once.</span>}
        </label>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-2 text-[13px] text-slate-700">
            <input type="checkbox" checked={withContacts} onChange={(e) => setWithContacts(e.target.checked)} /> Include the contact page
          </label>
          <InfoTip label="What the contact page has">Each tech's cell number and the call order: primary first, then backup, then the office.</InfoTip>
        </div>
        {withContacts && contacts && (
          <ul className="grid grid-cols-2 gap-x-4 rounded-lg bg-slate-50 px-3 py-2 text-[12.5px] text-slate-700">
            {contacts.map((c) => <li key={c.empno} className="flex justify-between"><span>{c.name}</span><span className="tabular-nums text-slate-500">{c.cell ?? "— no cell on the roster"}</span></li>)}
          </ul>
        )}
        <div className="rounded-lg bg-slate-50 px-3 py-2 text-[12.5px] text-slate-600">
          {period.sent_to_ceas_at
            ? changed.length > 0
              ? <>Changed since it last went out ({fmtShort(period.sent_to_ceas_at.slice(0, 10))}): <strong>{changed.map((d) => `${fmtShort(d.day)} ${nameOf(d.primary_empno)}${d.backup_empno ? " / " + nameOf(d.backup_empno) : ""}`).join(" · ")}</strong></>
              : <>Nothing has changed since it last went out ({fmtShort(period.sent_to_ceas_at.slice(0, 10))}).</>
            : <>This period has not been sent to CEAS yet.</>}
        </div>
        {result && <div className={cn("rounded-lg px-3 py-2 text-[12.5px]", result.ok ? "bg-emerald-50 text-emerald-800" : "bg-amber-50 text-amber-900")}>{result.text}</div>}
        <div className="flex flex-wrap items-center gap-2 pt-1">
          <Button size="sm" variant="ghost" onClick={() => window.open(`/team/oncall/print?period=${period.id}${withContacts ? "&contacts=1" : ""}`, "_blank", "noopener")}><Printer className="mr-1 h-3.5 w-3.5" /> Print / PDF</Button>
          <Button size="sm" variant="ghost" loading={marking} onClick={onMarkSent} title="I sent it another way (printed, faxed, forwarded)">Mark as sent</Button>
          <span className="ml-auto flex gap-2">
            <Button size="sm" variant="secondary" onClick={onClose}>Close</Button>
            <Button size="sm" loading={sending} disabled={!addr || !/@/.test(addr)} onClick={() => void send()}><Mail className="mr-1 h-3.5 w-3.5" /> Send</Button>
          </span>
        </div>
      </div>
    </Modal>
  );
}
